import React, { useEffect } from "react";

import { FoodDetailPanel } from "./FoodDetailPanel";
import { Spinner } from "./shared";
import { PassioFood } from "./types";

/**
 * Right-side slide-in drawer that hosts a {@link FoodDetailPanel}. Reusable for
 * previewing any food (existing matches, proposed records, search results).
 * Handles its own backdrop, escape-to-close, loading and error states.
 */
export interface FoodDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  error?: string;
  food?: PassioFood | null;
}

export const FoodDetailDrawer: React.FC<FoodDetailDrawerProps> = ({
  open,
  onClose,
  title,
  subtitle,
  loading,
  error,
  food,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
              {subtitle || "Food details"}
            </div>
            {title && (
              <div className="truncate text-sm font-medium text-gray-700">
                {title}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Generous bottom padding so the last content (portions, label link,
            actions) can scroll clear of the floating alert stack that lives in
            the bottom-right corner and would otherwise cover it. */}
        <div className="flex-1 overflow-y-auto px-4 pb-32 pt-4">
          {loading && (
            <div className="flex h-32 items-center justify-center">
              <Spinner label="Loading food details…" />
            </div>
          )}
          {!loading && error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          {!loading && !error && food && <FoodDetailPanel food={food} />}
          {!loading && !error && !food && (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
              No food details available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoodDetailDrawer;
